import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBeads } from '@/lib/api';
import type { ApiBead } from '@/lib/api';
import { useEditorStore } from '@/stores/editor-store';
import type { EditorPaletteColor } from '@/stores/editor-store';
import { BeadInfo } from './bead-info';

type SearchMode = 'text' | 'color';

const LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

interface BeadBrowserProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

export function BeadBrowser({ isOpen, onClose }: BeadBrowserProps) {
  const [searchMode, setSearchMode] = useState<SearchMode>('text');
  const [textQuery, setTextQuery] = useState('');
  const [colorQuery, setColorQuery] = useState('#FF0000');
  const [finishFilter, setFinishFilter] = useState('');

  const palette = useEditorStore((s) => s.palette);
  const addPaletteColor = useEditorStore((s) => s.addPaletteColor);
  const setActiveColorIndex = useEditorStore((s) => s.setActiveColorIndex);

  const searchParams = {
    ...(searchMode === 'text' && textQuery.length >= 2 ? { search: textQuery } : {}),
    ...(searchMode === 'color' ? { color: colorQuery } : {}),
    ...(finishFilter ? { finish: finishFilter } : {}),
    limit: 30,
  };

  const hasSearch = searchMode === 'color' || textQuery.length >= 2;

  const { data, isLoading } = useQuery({
    queryKey: ['beads', searchParams],
    queryFn: () => fetchBeads(searchParams),
    enabled: isOpen && hasSearch,
    staleTime: 30_000,
  });

  const paletteBeadIds = new Set(palette.map((p) => p.beadId));

  const handleAddBead = useCallback(
    (bead: ApiBead) => {
      if (paletteBeadIds.has(bead.id)) return;

      const nextIndex = palette.length + 1;
      const label = LABELS[nextIndex - 1] ?? `${nextIndex}`;

      const newColor: EditorPaletteColor = {
        index: nextIndex,
        beadId: bead.id,
        beadCode: bead.code,
        beadHex: bead.colorHex,
        label,
        count: 0,
      };

      addPaletteColor(newColor);
      setActiveColorIndex(nextIndex);
    },
    [palette, paletteBeadIds, addPaletteColor, setActiveColorIndex],
  );

  if (!isOpen) return null;

  const beads = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30" onClick={onClose} />

      {/* Bottom sheet */}
      <div className="flex max-h-[75dvh] flex-col rounded-t-2xl bg-white shadow-xl">
        {/* Handle + header */}
        <div className="flex flex-col items-center px-4 pt-3 pb-2">
          <div className="mb-3 h-1 w-10 rounded-full bg-gray-300" />
          <div className="flex w-full items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Przeglądarka koralików</h2>
            <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600">
              Zamknij
            </button>
          </div>
        </div>

        {/* Search mode tabs */}
        <div className="flex gap-1 px-4 pb-2">
          <button
            onClick={() => setSearchMode('text')}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              searchMode === 'text'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Szukaj po nazwie
          </button>
          <button
            onClick={() => setSearchMode('color')}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              searchMode === 'color'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Szukaj po kolorze
          </button>
        </div>

        {/* Search input */}
        <div className="px-4 pb-2">
          {searchMode === 'text' ? (
            <input
              type="text"
              value={textQuery}
              onChange={(e) => setTextQuery(e.target.value)}
              placeholder="Wpisz kod lub nazwę (np. DB0010, Black)..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
          ) : (
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={colorQuery}
                onChange={(e) => setColorQuery(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-md border border-gray-300"
              />
              <input
                type="text"
                value={colorQuery}
                onChange={(e) => setColorQuery(e.target.value)}
                placeholder="#FF0000"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <span className="shrink-0 text-xs text-gray-400">CIEDE2000</span>
            </div>
          )}
        </div>

        {/* Finish filter */}
        <div className="flex gap-1 overflow-x-auto px-4 pb-2">
          {['', 'opaque', 'matte', 'metallic', 'silver_lined', 'ceylon', 'luster', 'dyed', 'galvanized'].map((f) => (
            <button
              key={f}
              onClick={() => setFinishFilter(f)}
              className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
                finishFilter === f
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {f === '' ? 'Wszystkie' : f.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {!hasSearch && (
            <p className="py-8 text-center text-sm text-gray-400">
              {searchMode === 'text'
                ? 'Wpisz co najmniej 2 znaki...'
                : 'Wybierz kolor aby znaleźć koraliki'}
            </p>
          )}

          {isLoading && <p className="py-8 text-center text-sm text-gray-400">Szukam...</p>}

          {hasSearch && !isLoading && beads.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-400">Brak wyników</p>
          )}

          {beads.length > 0 && (
            <>
              <p className="mb-2 text-[10px] text-gray-400">
                {total} wyników{searchMode === 'color' ? ' (sortowane po ΔE)' : ''}
              </p>
              <div className="flex flex-col gap-2">
                {beads.map((bead) => (
                  <BeadInfo
                    key={bead.id}
                    bead={bead}
                    onAdd={handleAddBead}
                    isInPalette={paletteBeadIds.has(bead.id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
